// A parent's voice-follow choices and the consent version that records them
// (docs/plan/voice-follow-design.md, Open question 2, decided 2026-10-18).
//
// The choices live in `study.consent.consent_version` itself, so the funnel's
// Convex denorm, the consent gate and the research participant row all carry
// them without a column of their own. Every string this module renders is
// persisted and read back by parsing, so the grammar is a data contract:
// change it only with a new revision, never in place.

import * as z from "zod";

export interface VoiceChoices {
  /** Audio and what the app heard, sent to our server for research (tier 1). */
  readonly research: boolean;
  /** Audio sent to our server to train speech models (tier 3). */
  readonly training: boolean;
}

export const NO_VOICE_CHOICES: VoiceChoices = {
  research: false,
  training: false,
};

// Fails closed: a reply from a service that predates voice consent reads as
// tier 0, never as a grant.
export const voiceChoicesSchema = z
  .object({ research: z.boolean(), training: z.boolean() })
  .default(NO_VOICE_CHOICES);

/**
 * The design's tiers. Tier 0 is recognition on the device with nothing kept,
 * and needs no consent; tier 2 (live cloud recognition) was dropped.
 */
export type VoiceTier = 0 | 1 | 3;

export function voiceTiersOf(choices: VoiceChoices): readonly VoiceTier[] {
  const tiers: VoiceTier[] = [
    ...(choices.research ? [1 as const] : []),
    ...(choices.training ? [3 as const] : []),
  ];
  return tiers.length === 0 ? [0] : tiers;
}

const VOICE_MARK = "+vf";
const RESEARCH_PART = ".research";
const TRAINING_PART = ".training";
const BASE_VERSION = /^[a-z0-9][a-z0-9-]*$/;
const VERSION_WITH_VOICE =
  /^([a-z0-9][a-z0-9-]*)\+vf([1-9][0-9]*)(\.research)?(\.training)?$/;

/**
 * The consent version a parent signs: the study document's base version, the
 * voice addendum's revision, and one part per opt-in, in a fixed order — so
 * the same choices always render the same id. Choosing neither still carries
 * the `+vf<revision>` mark, which records that the voice step was shown and
 * declined rather than never asked.
 */
export function voiceConsentVersion(
  baseVersion: string,
  addendumRevision: number,
  choices: VoiceChoices,
): string {
  if (!BASE_VERSION.test(baseVersion)) {
    throw new Error(`not a base consent version: ${baseVersion}`);
  }
  if (!Number.isInteger(addendumRevision) || addendumRevision < 1) {
    throw new Error(`not an addendum revision: ${addendumRevision}`);
  }
  return [
    baseVersion,
    VOICE_MARK,
    String(addendumRevision),
    choices.research ? RESEARCH_PART : "",
    choices.training ? TRAINING_PART : "",
  ].join("");
}

export type ParsedConsentVersion =
  | { kind: "base"; base: string }
  | {
      kind: "voice";
      base: string;
      addendumRevision: number;
      choices: VoiceChoices;
    };

/** Null for a version this grammar does not produce. */
export function parseConsentVersion(
  version: string,
): ParsedConsentVersion | null {
  if (BASE_VERSION.test(version)) return { kind: "base", base: version };
  const match = VERSION_WITH_VOICE.exec(version);
  const [, base, revision, research, training] = match ?? [];
  if (base === undefined || revision === undefined) return null;
  return {
    kind: "voice",
    base,
    addendumRevision: Number(revision),
    choices: {
      research: research !== undefined,
      training: training !== undefined,
    },
  };
}

/**
 * Which voice addendum revisions may be signed under each study consent
 * document, one document per language. The one list the sign route, the
 * grant parser and the funnel all read. A revision is added for a language
 * only once the IRB and counsel have approved its wording in that language,
 * and a machine translation only after a fluent human's review: revision 1
 * (its retention a placeholder), every revision still marked DRAFT, and the
 * Spanish machine translation are absent, so none of them can be signed or
 * grant anything.
 */
export type SignableConsentVersions = Readonly<
  Record<string, readonly number[]>
>;

export const SIGNABLE_CONSENT_VERSIONS: SignableConsentVersions = {
  v1: [],
  "v1-es": [],
};

/** Whether a parent may sign this version: a listed study document, alone
 *  or with one of its listed addendum revisions. */
export function isSignableConsentVersion(
  version: string,
  signable: SignableConsentVersions = SIGNABLE_CONSENT_VERSIONS,
): boolean {
  const parsed = parseConsentVersion(version);
  if (parsed === null) return false;
  const revisions = signable[parsed.base];
  if (revisions === undefined) return false;
  return parsed.kind === "base" || revisions.includes(parsed.addendumRevision);
}

/**
 * What a signed version grants. A version signed before the voice step
 * existed, one this grammar cannot read, and one whose revision is not
 * signable grant nothing beyond tier 0, whatever its parts say.
 */
export function voiceChoicesOfVersion(
  version: string,
  signable: SignableConsentVersions = SIGNABLE_CONSENT_VERSIONS,
): VoiceChoices {
  const parsed = parseConsentVersion(version);
  return parsed?.kind === "voice" && isSignableConsentVersion(version, signable)
    ? parsed.choices
    : NO_VOICE_CHOICES;
}

export const VOICE_PURPOSES = [
  "research",
  "training",
] as const satisfies readonly (keyof VoiceChoices)[];
export type VoicePurpose = (typeof VOICE_PURPOSES)[number];

const purposesSchema = z
  .array(z.enum(VOICE_PURPOSES))
  .min(1)
  .max(VOICE_PURPOSES.length);

/** The signing parent's `POST /secure/voice/readers/:readerId/withdraw`. */
export const voiceWithdrawRequestSchema = z.strictObject({
  purposes: purposesSchema,
});
export type VoiceWithdrawRequest = z.infer<typeof voiceWithdrawRequestSchema>;

export const voiceWithdrawPath = (readerId: string): string =>
  `/secure/voice/readers/${encodeURIComponent(readerId)}/withdraw`;

/** Staff's `POST /secure/research/voice-withdraw`, on the research lane. */
export const STAFF_VOICE_WITHDRAW_PATH = "/secure/research/voice-withdraw";

export const staffVoiceWithdrawRequestSchema = z.strictObject({
  readerId: z.string().min(1),
  purposes: purposesSchema,
});
export type StaffVoiceWithdrawRequest = z.infer<
  typeof staffVoiceWithdrawRequestSchema
>;

/**
 * `consents` counts the reader's live consent rows; `detailObjectsMarked`
 * the stored objects newly marked for deletion, 0 on a retry.
 */
export const voiceWithdrawResponseSchema = z.object({
  consents: z.number().int().nonnegative(),
  detailObjectsMarked: z.number().int().nonnegative(),
});
export type VoiceWithdrawResponse = z.infer<typeof voiceWithdrawResponseSchema>;
